import { useState, useEffect, useCallback } from 'react';
import DueTodayPanel from './DueTodayPanel';
import { createPortal } from 'react-dom';

const API = 'https://tcio-api.onrender.com';

// ── STYLES (declared first — hoisting) ─────────────────────────
const s = {
  container: { padding: '32px', maxWidth: '1180px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { margin: '0 0 4px', fontSize: '24px', color: '#336090' },
  subtitle: { margin: 0, color: '#666', fontSize: '14px' },
  refreshBtn: { backgroundColor: '#336090', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },

  riskBanner: { display: 'flex', alignItems: 'center', gap: '28px', background: '#FDECEE', border: '1px solid #F5C6CB', borderLeft: '4px solid #E5484D', borderRadius: '12px', padding: '18px 24px', marginBottom: '18px' },
  riskBig: { fontSize: '34px', fontWeight: '800', color: '#E5484D', lineHeight: 1 },
  riskLabel: { fontSize: '11px', fontWeight: '700', color: '#5D6B7A', textTransform: 'uppercase', letterSpacing: '0.09em', marginTop: '5px' },
  riskDiv: { width: '1px', height: '38px', background: '#F0D4D7' },
  riskNote: { marginLeft: 'auto', fontSize: '12px', color: '#5D6B7A', maxWidth: '300px', lineHeight: 1.5, textAlign: 'right' },

  laneSummary: { display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '8px', marginBottom: '18px' },
  laneCard: { background: 'white', border: '2px solid #e5e7eb', borderRadius: '10px', padding: '11px 12px', cursor: 'pointer' },
  laneCount: { fontSize: '24px', fontWeight: '700', lineHeight: 1, marginBottom: '3px' },
  laneLabel: { fontSize: '9px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.03em' },
  laneMoney: { fontSize: '11px', fontWeight: '600', marginTop: '3px' },

  searchBar: { position: 'relative', marginBottom: '16px' },
  searchInput: { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' },
  clearBtn: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#999', fontSize: '16px' },

  cardList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  card: { background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', borderLeft: '5px solid transparent' },
  dcBanner: { padding: '6px 20px', background: '#0891b2', color: 'white', fontSize: '11px', fontWeight: '700', letterSpacing: '0.03em' },
  whyBar: { padding: '7px 20px', fontSize: '12px', fontWeight: '600', borderBottom: '1px solid rgba(0,0,0,0.05)' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '13px 20px 6px' },
  cardLeft: { flex: 1 },
  cardRight: { marginLeft: '16px', textAlign: 'right' },
  cardName: { fontSize: '16px', fontWeight: '700', color: '#1a2a3a', marginBottom: '4px' },
  cardMeta: { fontSize: '13px', color: '#555', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  authInfo: { fontSize: '12px', color: '#555' },
  noAuth: { fontSize: '12px', color: '#ef4444', fontWeight: '700' },
  urDue: { fontSize: '12px', color: '#666', marginTop: '3px' },
  moneyBig: { fontSize: '20px', fontWeight: '800', color: '#991b1b', lineHeight: 1 },
  moneySub: { fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '3px', fontWeight: '700' },
  statusTag: { display: 'inline-block', padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  internalTag: { display: 'inline-block', padding: '2px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', background: '#ede9fe', color: '#6d28d9' },

  cardActions: { display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '9px 20px 13px' },
  btnPrimary: { padding: '6px 14px', background: '#336090', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnGreen: { padding: '6px 14px', background: '#166534', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnRed: { padding: '6px 14px', background: '#991b1b', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnOutline: { padding: '6px 14px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' },
  btnClose: { padding: '6px 14px', background: '#475569', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  locBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700' },
  statusBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' },
  empty: { textAlign: 'center', padding: '60px', color: '#999', background: 'white', borderRadius: '12px' },

  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalBox: { background: 'white', borderRadius: '16px', width: '100%', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '20px 24px', background: '#336090', position: 'relative' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: 'white' },
  modalSub: { fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginTop: '3px' },
  modalClose: { position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', fontSize: '14px' },
  modalBody: { padding: '20px 24px', overflowY: 'auto', maxHeight: '62vh' },
  modalFooter: { padding: '16px 24px', borderTop: '1px solid #f0f4f8', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  saveBtn: { padding: '9px 20px', background: '#336090', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  cancelBtn: { padding: '9px 20px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  formGrid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' },
  hint: { fontSize: '11px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '8px 10px', gridColumn: '1 / -1', lineHeight: 1.5 },
  hintPurple: { fontSize: '12px', color: '#5b21b6', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '10px 12px', lineHeight: 1.55, marginBottom: '14px' },
  errBox: { fontSize: '12px', color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '9px 11px', marginBottom: '12px', lineHeight: 1.5 },

  detailSection: { padding: '16px 24px', borderBottom: '1px solid #f0f4f8' },
  sectionTitle: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#999', marginBottom: '12px' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  detailLabel: { fontSize: '11px', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' },
  detailValue: { fontSize: '14px', color: '#1a2a3a', fontWeight: '500' },
  loadingText: { color: '#999', fontSize: '13px', padding: '12px 0' },
  emptyText: { color: '#999', fontSize: '13px', padding: '12px 0' },
  authTable: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  authTh: { padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb' },
  authTd: { padding: '10px', borderBottom: '1px solid #f0f4f8', verticalAlign: 'middle' },
  editBtn: { padding: '4px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' },
  noteRow: { padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e8f0f7' },
  noteMeta: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '4px' },
  noteAction: { fontSize: '11px', fontWeight: '700', color: '#336090', background: '#e8f0f7', padding: '2px 8px', borderRadius: '4px' },
  noteStaff: { fontSize: '11px', fontWeight: '600', color: '#555' },
  noteDate: { fontSize: '11px', color: '#999' },
  noteText: { fontSize: '13px', color: '#374151', lineHeight: 1.5 },
};

// ── LANES ──────────────────────────────────────────────────────
const STATUS = {
  MISSED:        { label: 'Missed',      bar: '#E5484D', bg: '#FDECEE', text: '#C03540', tag: '#E5484D', tagBg: '#FBD9DC' },
  EXPIRED:       { label: 'Expired',     bar: '#E5484D', bg: '#FDECEE', text: '#C03540', tag: '#E5484D', tagBg: '#FBD9DC' },
  NO_DATES:      { label: 'No Dates',    bar: '#E99A18', bg: '#FFF6E5', text: '#A66B0B', tag: '#E99A18', tagBg: '#FCEBC9' },
  PAYER_STALL:   { label: 'Payer Stall', bar: '#E99A18', bg: '#FFF6E5', text: '#A66B0B', tag: '#E99A18', tagBg: '#FCEBC9' },
  POST_DC:       { label: 'Post-DC',     bar: '#18A6B8', bg: '#E8F7F9', text: '#0E7280', tag: '#18A6B8', tagBg: '#D2EFF3' },
  EXPIRING:      { label: 'Expiring',    bar: '#E99A18', bg: '#FFF6E5', text: '#A66B0B', tag: '#E99A18', tagBg: '#FCEBC9' },
  PENDING_SLOW:  { label: 'Pending — Slow', bar: '#E99A18', bg: '#FFF6E5', text: '#A66B0B', tag: '#E99A18', tagBg: '#FCEBC9' },
  PENDING:       { label: 'Pending',     bar: '#85AACF', bg: '#FFFFFF', text: '#336090', tag: '#4479B0', tagBg: '#E3EDF6' },
  ACTIVE:        { label: 'Covered',     bar: '#16A36A', bg: '#FFFFFF', text: '#0E7C50', tag: '#16A36A', tagBg: '#EAF8F1' },
  POST_DC_CLEAR: { label: 'DC — Clear',  bar: '#5D6B7A', bg: '#F4F8FB', text: '#5D6B7A', tag: '#5D6B7A', tagBg: '#E4EAF0' },
};

const ACTION_TYPES = [
  'Submitted Clinical', 'CCR Submission', 'Step-Down Request',
  'Auth Received', 'Auth Denied', 'No Auth Required',
  'P2P Requested', 'P2P Completed', 'Appeal Filed',
  'CEO Decision', 'Note',
];
const AUTH_STATUSES = ['Pending', 'Approved', 'Partial', 'Denied', 'Expired'];
const AUTH_TYPES = [
  { key: 'PAYER',    label: 'Payer Authorization — the insurance company approved it' },
];
const RETRO_OUTCOMES = ['', 'pending', 'paid', 'partial', 'denied'];
const LOC_OPTIONS = ['DTX', 'RTC', 'PHP', 'IOP', 'PHP MH', 'IOP MH'];
const PAYERS = ['Health Net','Anthem Blue Cross California','Triwest Healthcare','BCBS Hawaii','BCBS Florida','BCBS Louisiana','BCBS Michigan','BCBS South Carolina','BCBS North Carolina','BCBS Alabama','BCBS Texas','BCBS Oklahoma','BCBS Wyoming','BCBS Colorado','BCBS Missouri','BCBS Montana','BCBS Tennessee','BCBS Illinois','BCBS Kansas City','Capital BC Pennsylvania','Anthem BCBS Virginia','Anthem Central Region','Highmark BCBS','Highmark BCBS West Virginia','Empire BCBS New York','Horizon BCBS New Jersey','Wellmark BCBS','Premera Blue Cross','Blue Cross Federal Employee','Carefirst BCBS','Cigna','Aetna','UnitedHealthcare','UMR','Oxford','Magellan','Ambetter','Tricare West','GEHA','Kempton Group','Carelon','The Alliance','Blue Shield of California','VA','Other'];

// Canonical payer dropdown (API-backed) — same as BedBoard's
let PAYER_CACHE_UR = null;
function PayerSelect({ value, onChange, inputStyle }) {
  const [list, setList] = useState(PAYER_CACHE_UR || []);
  useEffect(() => {
    if (PAYER_CACHE_UR) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/payers/canonical`, { headers: { Authorization: `Bearer ${localStorage.getItem('tcio_token')}` } });
        const j = await r.json();
        if (j.ok) { PAYER_CACHE_UR = j.payers; setList(j.payers); }
      } catch (e) {}
    })();
  }, []);
  const addNew = async () => {
    const name = window.prompt('New payer name (exactly as the payer bills, e.g. BCBS OF NEVADA):');
    if (!name || name.trim().length < 3) return;
    try {
      const r = await fetch(`${API}/api/payers/canonical`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('tcio_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const j = await r.json();
      if (j.ok) { PAYER_CACHE_UR = [...(PAYER_CACHE_UR || []), j.name]; setList(PAYER_CACHE_UR); onChange(j.name); }
      else window.alert(j.error || 'Failed to add payer');
    } catch (e) { window.alert('Network error'); }
  };
  const sel = (v) => { if (v === '__ADD__') addNew(); else onChange(v); };
  return (
    <select style={inputStyle} value={list.includes(value) ? value : (value ? '__CURRENT__' : '')} onChange={e => sel(e.target.value)}>
      <option value="">Select...</option>
      {value && !list.includes(value) && <option value="__CURRENT__">{value}</option>}
      {list.map(p => <option key={p} value={p}>{p}</option>)}
      <option value="__ADD__">+ Add new payer&hellip;</option>
    </select>
  );
}
const DENIAL_REASONS = [
  '', 'Medical necessity', 'Auth lapse / late request', 'Records not received',
  'LOC not supported', 'Benefit exhausted', 'Non-covered service', 'Other',
];

// House standard when no payer auth is required. UR can go higher —
// medical necessity drives it — but this is the starting point.
const INTERNAL_DEFAULT_DAYS = { PHP: 30, IOP: 45, RTC: 14, DTX: 5 };

const LOC_COLORS = {
  DTX: { background: 'rgba(245,158,11,0.12)', color: '#d97706' },
  RTC: { background: 'rgba(59,130,246,0.12)', color: '#2563eb' },
  PHP: { background: 'rgba(16,185,129,0.12)', color: '#059669' },
  IOP: { background: 'rgba(139,92,246,0.12)', color: '#7c3aed' },
};

const money = (n) => '$' + Math.round(n || 0).toLocaleString('en-US');
const fmtDate = (d) => {
  if (!d) return '—';
  const s = (d instanceof Date ? d.toISOString() : String(d)).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
// N days INCLUSIVE of the start date
const addDays = (ymd, n) => {
  const d = new Date(ymd + 'T00:00:00');
  d.setDate(d.getDate() + n - 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

// ── ADD AUTH MODAL ─────────────────────────────────────────────
function AddAuthModal({ patient, token, onClose, onSaved }) {
  const _n = new Date();
  const today = `${_n.getFullYear()}-${String(_n.getMonth()+1).padStart(2,'0')}-${String(_n.getDate()).padStart(2,'0')}`;
  const [form, setForm] = useState({
    auth_type: 'PAYER',
    loc: patient.loc || '',
    date_from: today,
    date_to: '',
    days_requested: '',
    days_authorized: '',
    auth_number: '',
    facility_name: '',
    facility_npi: '',
    auth_status: 'Approved',
    submitted_by: '',
    denial_reason: '',
    p2p_requested: false,
    retro_billed: false,
    retro_outcome: '',
    auth_notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [npiOptions, setNpiOptions] = useState([]);
  const [prefillNote, setPrefillNote] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/ur/${patient.ur_patient_id}/latest-linked-submission`, {
      headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => {
        if (!j.ok || !j.submission) return;
        const sub = j.submission;
        setPrefillNote(sub);
        setForm(f => ({
          ...f,
          days_requested: f.days_requested || sub.days_requested || '',
          loc: f.loc || sub.loc_requested || '',
        }));
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.ur_patient_id]);

  useEffect(() => {
    if (!patient.facility_id) return;
    fetch(`${API}/api/facility-npis?facilityId=${patient.facility_id}`, {
      headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => {
        if (!j.ok) return;
        setNpiOptions(j.data);
        if (j.data.length === 1) {
          setForm(f => ({ ...f, facility_name: j.data[0].location_name, facility_npi: j.data[0].npi }));
        }
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.facility_id]);

  const isInternal = form.auth_type === 'INTERNAL';
  const req = parseInt(form.days_requested) || 0;
  const auth = parseInt(form.days_authorized) || 0;
  const deniedDays = req > auth ? req - auth : 0;
  // date inputs give ISO YYYY-MM-DD, so a plain string compare is a valid date compare
  const badDateRange = !!form.date_from && !!form.date_to && form.date_to < form.date_from;

  const setType = (t) => {
    if (t === 'INTERNAL') {
      const d = INTERNAL_DEFAULT_DAYS[form.loc] || 30;
      setForm(f => ({
        ...f, auth_type: t, auth_status: 'Approved', auth_number: '',
        days_requested: '',
        days_authorized: String(d),
        date_to: f.date_from ? addDays(f.date_from, d) : '',
      }));
    } else {
      setForm(f => ({ ...f, auth_type: t }));
    }
  };

  const setLoc = (loc) => {
    if (isInternal) {
      const d = INTERNAL_DEFAULT_DAYS[loc] || 30;
      setForm(f => ({
        ...f, loc,
        days_authorized: String(d),
        date_to: f.date_from ? addDays(f.date_from, d) : '',
      }));
    } else {
      setForm(f => ({ ...f, loc }));
    }
  };

  const setDays = (v) => {
    const n = parseInt(v) || 0;
    setForm(f => ({
      ...f, days_authorized: v,
      // Only auto-fill date_to when it is still blank, and never for IOP
      // (IOP days are sessions, not calendar days - 10 sessions can span 14 dates)
      date_to: (!f.date_to && f.loc !== 'IOP' && n > 0 && f.date_from) ? addDays(f.date_from, n) : f.date_to,
    }));
  };

  const setFrom = (v) => {
    const n = parseInt(form.days_authorized) || 0;
    setForm(f => ({
      ...f, date_from: v,
      date_to: (!f.date_to && f.loc !== 'IOP' && n > 0 && v) ? addDays(v, n) : f.date_to,
    }));
  };

  const save = async () => {
    if (!form.loc) { setErr('Level of care is required.'); return; }
    if (!form.days_authorized || !form.date_from || !form.date_to) {
      setErr('Days, date from, and date to are all required — including when no payer auth is needed. Clinical still commits to a specific number of days.');
      return;
    }
    if (badDateRange) { setErr('Approved Through cannot be before Date From.'); return; }
    setSaving(true); setErr('');
    try {
      const send = (extra) => fetch(`${API}/api/ur/${patient.ur_patient_id}/auths`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, episode_id: patient.episode_id, facility_id: patient.facility_id, ...(extra || {}) }),
      });
      let r = await send(); let j = await r.json();
      // UR LOCATION CHECK (Chad 10/5): the server asks when the level of care does not fit the location; a person confirms or corrects
      if (r.status === 409 && j.needs_location_confirm) {
        if (window.confirm(j.error + '\n\nOK = Yes, this is what the plan authorized.\nCancel = Go back and correct the location.') === false) { setSaving(false); return; }
        r = await send({ confirm_location: true }); j = await r.json();
      }
      if (!j.ok) { setErr(j.error || 'Failed to save'); setSaving(false); return; }
      onSaved();
      onClose();
    } catch (e) { setErr('Connection error'); }
    setSaving(false);
  };

  return (
    <div style={s.modalOverlay}>
      <div style={{ ...s.modalBox, maxWidth: '620px' }}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>Add Authorization</div>
          <div style={s.modalSub}>{patient.patient_name} · {patient.payer} · {patient.loc}</div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {err && <div style={s.errBox}>{err}</div>}

          <div style={{ ...s.field, marginBottom: '14px' }}>
            <label style={s.label}>Authorization Type</label>
            <select value={form.auth_type} onChange={e => setType(e.target.value)} style={s.input}>
              {AUTH_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>

          {isInternal && (
            <div style={s.hintPurple}>
              <strong>Internal authorization.</strong> The payer doesn't require prior auth — but clinical
              still commits to a number of days based on medical necessity, and that commitment is what we
              can defend in an audit or appeal. House standard:{' '}
              <strong>{INTERNAL_DEFAULT_DAYS[form.loc] || 30} days for {form.loc || 'this level of care'}</strong>.
              Go higher if the clinical picture supports it. <strong>Days and dates are required either way.</strong>
            </div>
          )}

          <div style={s.formGrid2}>
            <div style={s.field}><label style={s.label}>Level of Care *</label>
              <select value={form.loc} onChange={e => setLoc(e.target.value)} style={s.input}>
                <option value="">Select...</option>
                {LOC_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select></div>

            <div style={s.field}><label style={s.label}>Auth Status</label>
              <select value={form.auth_status} onChange={e => setForm({ ...form, auth_status: e.target.value })} style={s.input}>
                {AUTH_STATUSES.map(a => <option key={a} value={a}>{a}</option>)}
              </select></div>

            <div style={s.field}><label style={s.label}>Date From *</label>
              <input type="date" value={form.date_from} onChange={e => setFrom(e.target.value)} style={s.input} /></div>

            <div style={s.field}><label style={s.label}>Approved Through *</label>
              <input type="date" value={form.date_to} onChange={e => setForm({ ...form, date_to: e.target.value })}
                style={{ ...s.input, ...(badDateRange ? { borderColor: '#dc2626' } : {}) }} /></div>

            {badDateRange && (
              <div style={{ ...s.errBox, gridColumn: '1 / -1', marginBottom: 0 }}>
                Approved Through ({form.date_to}) is before Date From ({form.date_from}). Fix the dates to save.
              </div>
            )}

            {!isInternal && (
              <div style={s.field}><label style={s.label}>Days REQUESTED</label>
                <input type="number" value={form.days_requested}
                  onChange={e => setForm({ ...form, days_requested: e.target.value })} style={s.input} /></div>
            )}

            <div style={s.field}>
              <label style={s.label}>Days {isInternal ? 'APPROVED (clinical) *' : 'AUTHORIZED *'}</label>
              <input type="number" value={form.days_authorized} onChange={e => setDays(e.target.value)} style={s.input} />
            </div>

            {deniedDays > 0 && (
              <div style={s.hint}>
                <strong>{deniedDays} denied day{deniedDays > 1 ? 's' : ''}</strong> — asked for {req}, got {auth}.
                Log the reason and the retro-bill outcome below. That's how we learn what {patient.payer}
                actually pays back on denied days — a number nobody in this industry has about their own payers.
              </div>
            )}

            {!isInternal && (<>
              <div style={s.field}><label style={s.label}>Auth Number</label>
                <input value={form.auth_number} onChange={e => setForm({ ...form, auth_number: e.target.value })} style={s.input} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={s.label}>Program days (only if authorized for specific days, e.g. 3x/wk)</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => { const sel = (form.auth_days_of_week || []).includes(i); return (
                    <button key={d} type="button" onClick={() => setForm(f => { const cur = new Set(f.auth_days_of_week || []); if (cur.has(i)) cur.delete(i); else cur.add(i); return { ...f, auth_days_of_week: [...cur].sort() }; })}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid ' + (sel ? '#2563eb' : '#d1d5db'), background: sel ? '#2563eb' : '#fff', color: sel ? '#fff' : '#374151', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{d}</button>); })}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Leave blank for normal daily coverage. Checked days are the only days this auth covers; unchecked days show as no-program, not unauthorized.</div>
              </div>
            </>)}

            <div style={s.field}><label style={s.label}>Location / NPI</label>
              <select value={form.facility_npi ? form.facility_npi + '|' + form.facility_name : ''}
                onChange={e => {
                  const [npi, ...rest] = e.target.value.split('|');
                  setForm({ ...form, facility_npi: npi, facility_name: rest.join('|') });
                }} style={s.input}>
                <option value="">— select location —</option>
                {npiOptions.map(o => (
                  <option key={o.id} value={o.npi + '|' + o.location_name}>{o.location_name} — {o.npi}</option>
                ))}
              </select></div>

            <div style={s.field}>
              <label style={s.label}>{isInternal ? 'Approved By (clinician)' : 'Submitted By'}</label>
              <input value={form.submitted_by} onChange={e => setForm({ ...form, submitted_by: e.target.value })} placeholder="Initials" style={s.input} />
            </div>

            <div style={s.field}><label style={s.label}>Facility Name</label>
              <input value={form.facility_name} onChange={e => setForm({ ...form, facility_name: e.target.value })} style={s.input} /></div>
            <div style={s.field}><label style={s.label}>Facility NPI</label>
              <input value={form.facility_npi} onChange={e => setForm({ ...form, facility_npi: e.target.value })} style={s.input} /></div>

            {(form.auth_status === 'Denied' || form.auth_status === 'Partial' || deniedDays > 0) && (
              <>
                <div style={s.field}><label style={s.label}>Denial Reason</label>
                  <select value={form.denial_reason} onChange={e => setForm({ ...form, denial_reason: e.target.value })} style={s.input}>
                    {DENIAL_REASONS.map(d => <option key={d} value={d}>{d || 'Select...'}</option>)}
                  </select></div>
                <div style={s.field}><label style={s.label}>P2P Requested?</label>
                  <select value={form.p2p_requested ? 'yes' : 'no'} onChange={e => setForm({ ...form, p2p_requested: e.target.value === 'yes' })} style={s.input}>
                    <option value="no">No</option><option value="yes">Yes</option>
                  </select></div>
                <div style={s.field}><label style={s.label}>Retro-Billed?</label>
                  <select value={form.retro_billed ? 'yes' : 'no'} onChange={e => setForm({ ...form, retro_billed: e.target.value === 'yes' })} style={s.input}>
                    <option value="no">No</option><option value="yes">Yes</option>
                  </select></div>
                <div style={s.field}><label style={s.label}>Retro Outcome</label>
                  <select value={form.retro_outcome} onChange={e => setForm({ ...form, retro_outcome: e.target.value })} style={s.input}>
                    {RETRO_OUTCOMES.map(r => <option key={r} value={r}>{r || 'Not yet known'}</option>)}
                  </select></div>
              </>
            )}

            <div style={{ ...s.field, gridColumn: '1 / -1' }}><label style={s.label}>Notes</label>
              <textarea value={form.auth_notes} onChange={e => setForm({ ...form, auth_notes: e.target.value })}
                placeholder={isInternal ? 'Medical necessity justification…' : ''}
                style={{ ...s.input, height: '70px', resize: 'vertical' }} /></div>
          </div>
        </div>
        <div style={s.modalFooter}>
          <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
          <button onClick={save} disabled={saving || badDateRange}
            style={{ ...s.saveBtn, ...(saving || badDateRange ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
            {saving ? 'Saving...' : 'Add Auth'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ADD NOTE MODAL ─────────────────────────────────────────────
function AddNoteModal({ patient, token, defaultAction, onClose, onSaved }) {
  const [form, setForm] = useState({ action_type: defaultAction || 'Note', staff_initials: '', note_text: '' });
  const [saving, setSaving] = useState(false);
  const [readyNote, setReadyNote] = useState(null);
  const [linkNote, setLinkNote] = useState(true);
  const isSubmission = ['Submitted Clinical', 'CCR Submission'].includes(form.action_type);
  useEffect(() => {
    if (!isSubmission) return;
    fetch(`${API}/api/ur/${patient.ur_patient_id}/ready-emr-notes`, {
      headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => { if (j.ok && j.notes && j.notes.length) setReadyNote(j.notes[0]); })
      .catch(() => {});
  }, [isSubmission, patient.ur_patient_id, token]);

  const save = async () => {
    if (!form.note_text.trim()) return alert('Please enter a note.');
    setSaving(true);
    try {
      await fetch(`${API}/api/ur/${patient.ur_patient_id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, episode_id: patient.episode_id,
          emr_note_id: (isSubmission && readyNote && linkNote) ? readyNote.id : null }),
      });
      onSaved();
      onClose();
    } catch (err) { alert('Failed to save note'); }
    setSaving(false);
  };

  return (
    <div style={s.modalOverlay}>
      <div style={{ ...s.modalBox, maxWidth: '480px' }}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>{defaultAction || 'Add Note'}</div>
          <div style={s.modalSub}>{patient.patient_name}</div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {isSubmission && readyNote && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px',
              padding: '10px 14px', marginBottom: '12px', fontSize: '13px', color: '#166534' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={linkNote} onChange={e => setLinkNote(e.target.checked)} />
                <span><strong>Finalized UR note ready</strong>
                  {readyNote.loc_requested ? ` — ${readyNote.loc_requested}` : ''}
                  {readyNote.days_requested ? `, ${readyNote.days_requested} days requested` : ''}
                  . Link it to this submission.</span>
              </label>
            </div>
          )}
          <div style={s.formGrid2}>
            <div style={s.field}><label style={s.label}>Action Type</label>
              <select value={form.action_type} onChange={e => setForm({ ...form, action_type: e.target.value })} style={s.input}>
                {ACTION_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
              </select></div>
            <div style={s.field}><label style={s.label}>Staff Initials</label>
              <input value={form.staff_initials} onChange={e => setForm({ ...form, staff_initials: e.target.value })} placeholder="e.g. TW" style={s.input} /></div>
            <div style={{ ...s.field, gridColumn: '1 / -1' }}><label style={s.label}>Note</label>
              <textarea value={form.note_text} onChange={e => setForm({ ...form, note_text: e.target.value })}
                placeholder="Enter note..." style={{ ...s.input, height: '100px', resize: 'vertical' }} autoFocus /></div>
          </div>
        </div>
        <div style={s.modalFooter}>
          <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={s.saveBtn}>{saving ? 'Saving...' : 'Save Note'}</button>
        </div>
      </div>
    </div>
  );
}

// ── MARK GAP (coverage exception) MODAL ────────────────────────
const EXCEPTION_TYPES = [
  { key: 'scholarship', label: 'Scholarship — house is comping these days' },
  { key: 'denied_gap',  label: 'Denied gap — denial, resubmitting different LOC, no retro' },
  { key: 'dc_day',      label: 'Discharge day — not billed' },
  { key: 'other',       label: 'Other' },
];
const EXCEPTION_LABEL = { scholarship: 'Scholarship', denied_gap: 'Denied gap', dc_day: 'DC day', other: 'Other' };

const DG_COLOR = {
  covered:    { bg: '#dcfce7', fg: '#166534', label: 'Auth covered' },
  paid:       { bg: '#d1fae5', fg: '#065f46', label: 'Paid claim' },
  exception:  { bg: '#e0e7ff', fg: '#3730a3', label: 'Exception' },
  nonbilling: { bg: '#f1f5f9', fg: '#94a3b8', label: 'Non-billing' },
  uncovered:  { bg: '#fee2e2', fg: '#991b1b', label: 'UNCOVERED' },
};
function DayGridModal({ patient, token, onClose }) {
  const [grid, setGrid] = useState(null);
  const [err, setErr] = useState('');
  const [tip, setTip] = useState(null);
  const [sel, setSel] = useState(null);
  const [vForm, setVForm] = useState({ verdict: 'covered_extend', extend_auth_number: '', exception_type: 'other', detail: '' });
  const [vBusy, setVBusy] = useState(false);
  const loadGrid = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/ur/day-grid/${patient.episode_id}`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.ok) setGrid(j); else setErr(j.error || 'Failed to load');
    } catch (e) { setErr('Network error'); }
  }, [patient.episode_id, token]);
  useEffect(() => { loadGrid(); }, [loadGrid]);
  const submitVerdict = async () => {
    if (vForm.verdict === 'covered_extend' && !vForm.extend_auth_number) { setErr('Pick which auth covers this day.'); return; }
    setVBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/ur/day-verdict`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ episode_id: patient.episode_id, day: sel.date, ...vForm }),
      });
      const j = await r.json();
      if (j.ok) { setSel(null); setVForm({ verdict: 'covered_extend', extend_auth_number: '', exception_type: 'other', detail: '' }); await loadGrid(); }
      else setErr(j.error || 'Failed');
    } catch (e) { setErr('Network error'); }
    setVBusy(false);
  };
  const weeks = [];
  if (grid) {
    let week = new Array(7).fill(null);
    grid.days.forEach((d, i) => {
      if (i === 0) { for (let k = 0; k < d.dow; k++) week[k] = null; }
      week[d.dow] = d;
      if (d.dow === 6) { weeks.push(week); week = new Array(7).fill(null); }
    });
    if (week.some(x => x)) weeks.push(week);
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', width: '560px', maxWidth: '94vw', maxHeight: '86vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <div style={{ fontSize: '16px', fontWeight: 700 }}>Coverage &mdash; {patient.patient_name}</div>
          <button style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }} onClick={onClose}>Close</button>
        </div>
        {err && <div style={{ color: '#ef4444', fontSize: '13px' }}>{err}</div>}
        {!grid && !err && <div style={{ fontSize: '13px', color: '#64748b' }}>Loading&hellip;</div>}
        {grid && (
          <>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
              {grid.admit_date} to {grid.discharge_date || 'today'} &middot; <b style={{ color: grid.computed_unauth > 0 ? '#ef4444' : '#16a34a' }}>{grid.computed_unauth} uncovered day{grid.computed_unauth === 1 ? '' : 's'}</b>
              {grid.manual_unauth != null && grid.manual_unauth !== grid.computed_unauth &&
                <span style={{ color: '#f59e0b' }}> &middot; manual field says {grid.manual_unauth} (stale &mdash; grid is truth)</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', marginBottom: '4px', textAlign: 'center' }}>
              {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(d => <div key={d}>{d}</div>)}
            </div>
            {weeks.map((wk, wi) => (
              <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '4px' }}>
                {wk.map((d, di) => d ? (
                  <div key={di}
                    onMouseEnter={() => setTip(d)} onMouseLeave={() => setTip(null)}
                    onClick={() => { if (d.status === 'uncovered' || d.verdict) { setSel(d); setErr(''); } }}
                    style={{ background: DG_COLOR[d.status].bg, color: DG_COLOR[d.status].fg, borderRadius: '6px', padding: '6px 2px', textAlign: 'center',
                      cursor: (d.status === 'uncovered' || d.verdict) ? 'pointer' : 'default',
                      border: sel && sel.date === d.date ? '2px solid #2563eb' : d.status === 'uncovered' ? '1px solid #fca5a5' : '1px solid transparent',
                      opacity: d.verdict ? 0.75 : 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: 700 }}>{+d.date.slice(8,10)}{d.verdict ? ' ✓' : ''}</div>
                    <div style={{ fontSize: '9px' }}>{d.loc}</div>
                  </div>
                ) : <div key={di} />)}
              </div>
            ))}
            <div style={{ minHeight: '34px', marginTop: '8px', fontSize: '12px', color: '#334155', background: '#f8fafc', borderRadius: '6px', padding: '8px 10px' }}>
              {tip ? <><b>{tip.date}</b> &mdash; {tip.detail}{tip.verdict ? <span style={{ color: '#166534' }}> &middot; ✓ {tip.verdict.v.replace('_',' ')} by {tip.verdict.by}</span> : ''}</> : (sel ? 'Choose a verdict below' : 'Hover a day for details — click a red day to decide it')}
            </div>
            {sel && sel.verdict && (
              <div style={{ marginTop: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px', background: '#fff' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>{sel.date} — decided</div>
                <div style={{ fontSize: '12px', color: '#334155', marginBottom: '8px' }}>
                  ✓ <b>{sel.verdict.v.replace(/_/g, ' ')}</b> by {sel.verdict.by}{sel.verdict.detail ? ` — ${sel.verdict.detail}` : ''}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={async () => {
                    if (!window.confirm(`Remove the verdict on ${sel.date}? This also removes the exception/retro range it created.`)) return;
                    setVBusy(true);
                    try {
                      const r = await fetch(`${API}/api/ur/day-verdict`, {
                        method: 'DELETE', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ episode_id: patient.episode_id, day: sel.date }),
                      });
                      const j = await r.json();
                      if (j.ok) { if (j.note && j.note.includes('NOTE')) window.alert(j.note); setSel(null); await loadGrid(); }
                      else setErr(j.error || 'Failed');
                    } catch (e) { setErr('Network error'); }
                    setVBusy(false);
                  }} disabled={vBusy}
                    style={{ padding: '7px 14px', borderRadius: '6px', border: 'none', background: '#ef4444', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: vBusy ? 0.6 : 1 }}>
                    {vBusy ? 'Removing…' : 'Remove Verdict'}</button>
                  <button onClick={() => setSel(null)}
                    style={{ padding: '7px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
            {sel && !sel.verdict && (
              <div style={{ marginTop: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px', background: '#fff' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Decide {sel.date} ({sel.loc})</div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  {[['covered_extend','Covered — extend auth'],['retro_bill','Truly unauth — retro bill'],['not_billable','Truly unauth — not billable']].map(([k, l]) => (
                    <button key={k} onClick={() => setVForm({ ...vForm, verdict: k })}
                      style={{ padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        border: vForm.verdict === k ? '2px solid #2563eb' : '1px solid #cbd5e1',
                        background: vForm.verdict === k ? '#eff6ff' : '#fff', color: '#334155' }}>{l}</button>
                  ))}
                </div>
                {vForm.verdict === 'covered_extend' && (
                  <select value={vForm.extend_auth_number} onChange={e => setVForm({ ...vForm, extend_auth_number: e.target.value })}
                    style={{ width: '100%', padding: '7px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', marginBottom: '8px' }}>
                    <option value="">— Which auth covers this day? —</option>
                    {(grid.auths || []).map((a, ai) => (
                      <option key={ai} value={a.auth_number}>{a.auth_number} ({a.loc} {a.date_from} → {a.date_to})</option>
                    ))}
                  </select>
                )}
                {vForm.verdict === 'not_billable' && (
                  <select value={vForm.exception_type} onChange={e => setVForm({ ...vForm, exception_type: e.target.value })}
                    style={{ width: '100%', padding: '7px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', marginBottom: '8px' }}>
                    <option value="scholarship">Scholarship</option>
                    <option value="denied_gap">Denied gap</option>
                    <option value="dc_day">DC day</option>
                    <option value="other">Other</option>
                  </select>
                )}
                <input value={vForm.detail} onChange={e => setVForm({ ...vForm, detail: e.target.value })}
                  placeholder="Note (optional — why this verdict)"
                  style={{ width: '100%', padding: '7px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', marginBottom: '8px', boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={submitVerdict} disabled={vBusy}
                    style={{ padding: '7px 14px', borderRadius: '6px', border: 'none', background: '#16a34a', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: vBusy ? 0.6 : 1 }}>
                    {vBusy ? 'Saving…' : 'Save Verdict'}</button>
                  <button onClick={() => setSel(null)}
                    style={{ padding: '7px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px', fontSize: '11px' }}>
              {Object.entries(DG_COLOR).map(([k, v]) => (
                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: v.bg, border: '1px solid ' + v.fg, display: 'inline-block' }} />
                  {v.label}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MarkGapModal({ patient, token, onClose, onSaved }) {
  const [form, setForm] = useState({ date_from: '', date_to: '', exception_type: 'scholarship', reason: '' });
  const [existing, setExisting] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const loadExisting = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/ur/${patient.ur_patient_id}/exceptions`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.ok) setExisting(j.exceptions);
    } catch (e) {}
  }, [patient.ur_patient_id, token]);
  useEffect(() => { loadExisting(); }, [loadExisting]);

  const save = async () => {
    if (!form.date_from || !form.date_to) { setErr('Both dates are required.'); return; }
    if (form.date_to < form.date_from) { setErr('End date is before start date.'); return; }
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API}/api/ur/${patient.ur_patient_id}/exception`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || 'Failed to save'); setSaving(false); return; }
      onSaved(); onClose();
    } catch (e) { setErr('Connection error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Remove this exception? Those days go back to counting as unauthorized.')) return;
    try {
      await fetch(`${API}/api/ur/exception/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      loadExisting(); onSaved();
    } catch (e) { alert('Failed to remove'); }
  };

  return (
    <div style={s.modalOverlay}>
      <div style={{ ...s.modalBox, maxWidth: '520px' }}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>Mark Coverage Gap</div>
          <div style={s.modalSub}>{patient.patient_name} · gaps marked here stop counting as at-risk</div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {err && <div style={s.errBox}>{err}</div>}
          <div style={s.hintPurple}>
            Use this when a gap is <strong>known and accepted</strong>: a scholarship stay, a denial you are
            resubmitting at a different LOC, or a discharge day that will not be billed. The days stay visible
            but come out of the at-risk number.
          </div>
          <div style={s.formGrid2}>
            <div style={{ ...s.field, gridColumn: '1 / -1' }}><label style={s.label}>Reason Type *</label>
              <select value={form.exception_type} onChange={e => setForm({ ...form, exception_type: e.target.value })} style={s.input}>
                {EXCEPTION_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select></div>
            <div style={s.field}><label style={s.label}>From *</label>
              <input type="date" value={form.date_from} onChange={e => setForm({ ...form, date_from: e.target.value })} style={s.input} /></div>
            <div style={s.field}><label style={s.label}>Through *</label>
              <input type="date" value={form.date_to} onChange={e => setForm({ ...form, date_to: e.target.value })} style={s.input} /></div>
            <div style={{ ...s.field, gridColumn: '1 / -1' }}><label style={s.label}>Notes</label>
              <textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Scholarship 2 weeks while transitioning to sober living" style={{ ...s.input, height: '60px', resize: 'vertical' }} /></div>
          </div>
          {existing.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={s.sectionTitle}>Existing Exceptions</div>
              {existing.map(x => (
                <div key={x.id} style={{ ...s.noteRow, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div>
                    <span style={s.noteAction}>{EXCEPTION_LABEL[x.exception_type] || x.exception_type}</span>{' '}
                    <span style={{ fontSize: '12px', color: '#555' }}>{fmtDate(x.date_from)} → {fmtDate(x.date_to)}</span>
                    {x.reason && <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>{x.reason}</div>}
                  </div>
                  <button onClick={() => del(x.id)} style={{ ...s.editBtn, color: '#991b1b' }}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={s.modalFooter}>
          <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={s.saveBtn}>{saving ? 'Saving...' : 'Mark Gap'}</button>
        </div>
      </div>
    </div>
  );
}

// ── DISCHARGE MODAL (UR self-serve, emr perm 'discharge') ───
function DischargeModal({ patient, token, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ discharge_date: today, discharge_type: '', discharge_reason: '' });
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    if (!form.discharge_date || !form.discharge_type) { setErr('Discharge date and type are required.'); return; }
    if (!confirming) { setConfirming(true); return; }
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API}/api/discharge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          episode_id: patient.episode_id,
          discharge_date: form.discharge_date,
          discharge_type: form.discharge_type,
          discharge_reason: form.discharge_reason || null,
        }),
      });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || 'Failed to discharge'); setSaving(false); setConfirming(false); return; }
      onSaved(); onClose();
    } catch (e) { setErr('Connection error'); setConfirming(false); }
    setSaving(false);
  };

  return (
    <div style={s.modalOverlay}>
      <div style={{ ...s.modalBox, maxWidth: '440px' }}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>Discharge Patient</div>
          <div style={s.modalSub}>{patient.patient_name} · this is the official discharge record</div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#5D6B7A', textTransform: 'uppercase', marginBottom: 4 }}>Discharge Date</label>
            <input type="date" value={form.discharge_date}
              onChange={e => { setForm(f => ({ ...f, discharge_date: e.target.value })); setConfirming(false); }}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #D8E3EE', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#5D6B7A', textTransform: 'uppercase', marginBottom: 4 }}>Discharge Type</label>
            <select value={form.discharge_type}
              onChange={e => { setForm(f => ({ ...f, discharge_type: e.target.value })); setConfirming(false); }}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #D8E3EE', fontSize: 13, width: '100%', boxSizing: 'border-box', background: '#fff' }}>
              <option value="">Select…</option>
              <option value="planned">Planned</option>
              <option value="unplanned">Unplanned</option>
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#5D6B7A', textTransform: 'uppercase', marginBottom: 4 }}>Reason (optional)</label>
            <input value={form.discharge_reason}
              onChange={e => setForm(f => ({ ...f, discharge_reason: e.target.value }))}
              placeholder="e.g. ACA, completed program"
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #D8E3EE', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
          </div>
          {confirming && (
            <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#92400E', marginBottom: 12 }}>
              Confirm: this discharges <strong>{patient.patient_name}</strong> effective {form.discharge_date}, frees their bed, and closes the episode across the system. Click Discharge again to confirm.
            </div>
          )}
          {err && <div style={{ color: '#B91C1C', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ background: 'none', border: '1px solid #D8E3EE', borderRadius: 7, padding: '8px 14px', fontSize: 12.5, cursor: 'pointer', color: '#5D6B7A' }}>Cancel</button>
            <button disabled={saving} onClick={save}
              style={{ background: confirming ? '#B91C1C' : '#0878E8', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Discharging…' : confirming ? 'Discharge — Confirm' : 'Discharge'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── EDIT PATIENT INFO MODAL (admit date / payer / member id) ───
// Precert + ICM block (Tina bf0e1141). Precert info is PER-PAYER (payer_precert_contacts,
// entered once, shown on every case with that payer). ICM is PER-CASE (ur_patients.cm_contact).
function PrecertIcmBlock({ patient, token }) {
  const [pc, setPc] = useState(null);
  const [editPc, setEditPc] = useState(null);
  const [icm, setIcm] = useState(patient.cm_contact || '');
  const [editIcm, setEditIcm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!patient.payer) return;
    fetch(`${API}/api/ur/precert-contact?payer=${encodeURIComponent(patient.payer)}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(j => { if (j.ok) setPc(j.contact); }).catch(() => {});
  }, [patient.payer, token]);
  const savePc = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/ur/precert-contact`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ payer: patient.payer, ...editPc }),
      });
      const j = await r.json();
      if (j.ok) { setPc(j.contact); setEditPc(null); }
    } catch (e) {}
    setBusy(false);
  };
  const saveIcm = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/ur/${patient.ur_patient_id || patient.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cm_contact: icm }),
      });
      const j = await r.json();
      if (j.ok) setEditIcm(false);
    } catch (e) {}
    setBusy(false);
  };
  const row = { display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '3px 0' };
  const lab = { color: '#6b7280', fontWeight: 600 };
  const ed = { fontSize: '12px', color: '#2563eb', cursor: 'pointer', fontWeight: 700 };
  const inp = { width: '100%', padding: '6px 9px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
  return (
    <div style={s.detailSection}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={s.sectionTitle}>Precert &amp; ICM</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
        <div>
          <div style={{ ...lab, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>
            Precert - {patient.payer || 'no payer'} <span style={{ color: '#9ca3af', textTransform: 'none', letterSpacing: 0 }}>(shared across all this payer's cases)</span>
            {!editPc && patient.payer && <span style={{ ...ed, marginLeft: 8 }} onClick={() => setEditPc({ precert_phone: pc?.precert_phone || '', precert_fax: pc?.precert_fax || '', portal_url: pc?.portal_url || '', portal_notes: pc?.portal_notes || '' })}>Edit</span>}
          </div>
          {editPc ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <input style={inp} placeholder="Precert phone" value={editPc.precert_phone} onChange={e => setEditPc({ ...editPc, precert_phone: e.target.value })} />
              <input style={inp} placeholder="Precert fax" value={editPc.precert_fax} onChange={e => setEditPc({ ...editPc, precert_fax: e.target.value })} />
              <input style={inp} placeholder="Portal URL" value={editPc.portal_url} onChange={e => setEditPc({ ...editPc, portal_url: e.target.value })} />
              <input style={inp} placeholder="Portal notes (login hints, submission steps)" value={editPc.portal_notes} onChange={e => setEditPc({ ...editPc, portal_notes: e.target.value })} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={savePc} disabled={busy} style={s.saveBtn}>Save</button>
                <button onClick={() => setEditPc(null)} style={s.cancelBtn}>Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={row}><span style={lab}>Phone</span><span>{pc?.precert_phone || '—'}</span></div>
              <div style={row}><span style={lab}>Fax</span><span>{pc?.precert_fax || '—'}</span></div>
              <div style={row}><span style={lab}>Portal</span><span>{pc?.portal_url ? <a href={pc.portal_url.startsWith('http') ? pc.portal_url : 'https://' + pc.portal_url} target="_blank" rel="noopener noreferrer">{pc.portal_url}</a> : '—'}</span></div>
              {pc?.portal_notes && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>{pc.portal_notes}</div>}
            </div>
          )}
        </div>
        <div>
          <div style={{ ...lab, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>
            ICM (insurance case manager) <span style={{ color: '#9ca3af', textTransform: 'none', letterSpacing: 0 }}>(this patient)</span>
            {!editIcm && <span style={{ ...ed, marginLeft: 8 }} onClick={() => setEditIcm(true)}>Edit</span>}
          </div>
          {editIcm ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <input style={inp} placeholder="ICM name + phone/fax/email" value={icm} onChange={e => setIcm(e.target.value)} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={saveIcm} disabled={busy} style={s.saveBtn}>Save</button>
                <button onClick={() => { setEditIcm(false); setIcm(patient.cm_contact || ''); }} style={s.cancelBtn}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{icm || '—'}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function EditEpisodeModal({ patient, token, onClose, onSaved }) {
  const [form, setForm] = useState({
    admit_date: patient.admit_date ? String(patient.admit_date).slice(0, 10) : '',
    primary_payer: patient.payer || '',
    member_id: patient.member_id || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    if (!form.admit_date || !form.primary_payer) { setErr('Admit date and payer are required.'); return; }
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API}/api/episodes/${patient.episode_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || 'Failed to save'); setSaving(false); return; }
      onSaved(); onClose();
    } catch (e) { setErr('Connection error'); }
    setSaving(false);
  };

  return (
    <div style={s.modalOverlay}>
      <div style={{ ...s.modalBox, maxWidth: '440px' }}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>Edit Patient Info</div>
          <div style={s.modalSub}>{patient.patient_name} · changes apply everywhere instantly</div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {err && <div style={s.errBox}>{err}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={s.field}><label style={s.label}>Admit Date *</label>
              <input type="date" value={form.admit_date} onChange={e => setForm({ ...form, admit_date: e.target.value })} style={s.input} /></div>
            <div style={s.field}><label style={s.label}>Insurance / Payer *</label>
              <PayerSelect inputStyle={s.input} value={form.primary_payer} onChange={v => setForm({ ...form, primary_payer: v })} /></div>
            <div style={s.field}><label style={s.label}>Member ID</label>
              <input value={form.member_id} onChange={e => setForm({ ...form, member_id: e.target.value })} style={s.input} /></div>
          </div>
        </div>
        <div style={s.modalFooter}>
          <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={s.saveBtn}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ── CONFIRM RETRO BILL MODAL ───────────────────────────────────
function RetroBillModal({ patient, token, onClose, onSaved }) {
  const [form, setForm] = useState({ date_from: '', date_to: '', note: '' });
  const [existing, setExisting] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const loadExisting = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/ur/${patient.ur_patient_id}/retro`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.ok) setExisting(j.retros);
    } catch (e) {}
  }, [patient.ur_patient_id, token]);
  useEffect(() => { loadExisting(); }, [loadExisting]);

  const save = async () => {
    if (!form.date_from || !form.date_to) { setErr('Both dates are required.'); return; }
    if (form.date_to < form.date_from) { setErr('End date is before start date.'); return; }
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API}/api/ur/${patient.ur_patient_id}/retro`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || 'Failed to save'); setSaving(false); return; }
      onSaved(); onClose();
    } catch (e) { setErr('Connection error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Remove this retro-bill confirmation? Those days go back to awaiting auth.')) return;
    try {
      await fetch(`${API}/api/ur/retro/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      loadExisting(); onSaved();
    } catch (e) { alert('Failed to remove'); }
  };

  return (
    <div style={s.modalOverlay}>
      <div style={{ ...s.modalBox, maxWidth: '520px' }}>
        <div style={{ ...s.modalHeader, background: '#b45309' }}>
          <div style={s.modalTitle}>Confirm Retro Bill</div>
          <div style={s.modalSub}>{patient.patient_name} · UR confirms these unauthorized days WILL be billed</div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {err && <div style={s.errBox}>{err}</div>}
          <div style={s.hintPurple}>
            Days without an auth stay <strong>AWAITING AUTH</strong> and are held off the billing worksheet
            until confirmed here. Confirming tells billing: <strong>bill these days and retro-bill the auth.</strong>
            If the gap should NOT be billed (denial, scholarship), use Mark Gap instead.
          </div>
          <div style={s.formGrid2}>
            <div style={s.field}><label style={s.label}>From *</label>
              <input type="date" value={form.date_from} onChange={e => setForm({ ...form, date_from: e.target.value })} style={s.input} /></div>
            <div style={s.field}><label style={s.label}>Through *</label>
              <input type="date" value={form.date_to} onChange={e => setForm({ ...form, date_to: e.target.value })} style={s.input} /></div>
            <div style={{ ...s.field, gridColumn: '1 / -1' }}><label style={s.label}>Note</label>
              <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                placeholder="e.g. Payer confirmed retro review after auth lapse" style={{ ...s.input, height: '60px', resize: 'vertical' }} /></div>
          </div>
          {existing.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={s.sectionTitle}>Confirmed Retro Ranges</div>
              {existing.map(x => (
                <div key={x.id} style={{ ...s.noteRow, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div>
                    <span style={{ ...s.noteAction, background: '#ffedd5', color: '#b45309' }}>RETRO</span>{' '}
                    <span style={{ fontSize: '12px', color: '#555' }}>{fmtDate(x.date_from)} → {fmtDate(x.date_to)}</span>
                    {x.note && <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>{x.note}</div>}
                  </div>
                  <button onClick={() => del(x.id)} style={{ ...s.editBtn, color: '#991b1b' }}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={s.modalFooter}>
          <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...s.saveBtn, background: '#b45309' }}>{saving ? 'Saving...' : 'Confirm Retro Bill'}</button>
        </div>
      </div>
    </div>
  );
}

// ── PATIENT DETAIL MODAL ───────────────────────────────────────
function PatientDetailModal({ patient, token, onClose, onRefresh }) {
  const [showDayGrid, setShowDayGrid] = useState(false);
  const [showAddAuth, setShowAddAuth] = useState(false);
  const [auths, setAuths] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAuth, setEditAuth] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [noteForm, setNoteForm] = useState({});

  const startEditNote = (n) => {
    setEditNote(n.id);
    setNoteForm({ action_type: n.action_type || 'Note', staff_initials: n.staff_initials || '', note_text: n.note_text || '' });
  };

  const saveEditNote = async (noteId) => {
    setSaving(true);
    try {
      await fetch(`${API}/api/ur/notes/${noteId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(noteForm),
      });
      setEditNote(null);
      loadDetail();
      onRefresh();
    } catch (err) { alert('Failed to update note'); }
    setSaving(false);
  };

  const deleteNote = async (n) => {
    if (!window.confirm(`Delete this "${n.action_type}" note? A linked submission record will also be removed.`)) return;
    try {
      const r = await fetch(`${API}/api/ur/notes/${n.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!d.ok) return alert(d.error || 'Delete failed');
      loadDetail();
      onRefresh();
    } catch (err) { alert('Delete failed'); }
  };

  const loadDetail = useCallback(async () => {
    setLoading(true);
    const H = { Authorization: `Bearer ${token}` };
    try {
      const [a, n] = await Promise.all([
        fetch(`${API}/api/ur/${patient.ur_patient_id}/auths`, { headers: H }).then(r => r.json()),
        fetch(`${API}/api/ur/${patient.ur_patient_id}/notes`, { headers: H }).then(r => r.json()),
      ]);
      if (a.ok) setAuths(a.data);
      if (n.ok) setNotes(n.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [patient.ur_patient_id, token]);

  useEffect(() => { loadDetail(); }, [loadDetail]);


  const [npiOptions, setNpiOptions] = useState([]);
  useEffect(() => {
    if (!patient.facility_id) return;
    fetch(`${API}/api/facility-npis?facilityId=${patient.facility_id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(j => { if (j.ok) setNpiOptions(j.data); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.facility_id]);

  const startEditAuth = (a) => {
    setEditAuth(a.id);
    setEditForm({
      loc: a.loc || '',
      auth_type: 'PAYER',
      auth_days_of_week: Array.isArray(a.auth_days_of_week) ? a.auth_days_of_week.map(Number) : [],
      auth_number: a.auth_number || '',
      date_from: a.date_from ? a.date_from.split('T')[0] : '',
      date_to: a.date_to ? a.date_to.split('T')[0] : '',
      days_requested: a.days_requested || '',
      days_authorized: a.days_authorized || '',
      auth_status: a.auth_status || 'Approved',
      denial_reason: a.denial_reason || '',
      p2p_outcome: a.p2p_outcome || '',
      retro_outcome: a.retro_outcome || '',
      ceo_decision: a.ceo_decision || '',
      auth_notes: a.auth_notes || '',
      facility_name: a.facility_name || '',
      facility_npi: a.facility_npi || '',
    });
  };

  const saveEditAuth = async (authId) => {
    setSaving(true);
    try {
      const send = (extra) => fetch(`${API}/api/ur/auths/${authId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...editForm, ...(extra || {}) }),
      });
      let r = await send(); let j = await r.json().catch(() => ({}));
      if (r.status === 409 && j.needs_location_confirm) {
        if (window.confirm(j.error + '\n\nOK = Yes, this is what the plan authorized.\nCancel = Go back and correct the location.') === false) { setSaving(false); return; }
        r = await send({ confirm_location: true }); j = await r.json().catch(() => ({}));
      }
      if (r.ok === false) { alert(j.error || 'Failed to update auth'); setSaving(false); return; }
      setEditAuth(null);
      loadDetail();
      onRefresh();
    } catch (err) { alert('Failed to update auth'); }
    setSaving(false);
  };

  const canDeleteAuth = ['ceo', 'admin', 'ur_supervisor'].includes(localStorage.getItem('tcio_role'));
  const deleteAuth = async (a) => {
    const reason = prompt(`Delete this authorization? ${a.loc || ''} ${a.auth_number || ''} ${a.date_from ? a.date_from.slice(0, 10) : ''} - ${a.date_to ? a.date_to.slice(0, 10) : ''}\n\nThis removes it and recomputes billing. The full record is kept in the audit log. Reason:`, '');
    if (reason === null) return;
    try {
      const res = await fetch(`${API}/api/ur/auths/${a.id}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ reason }) });
      const j = await res.json().catch(() => null); if (!res.ok || !j || !j.ok) throw new Error((j && j.error) || `HTTP ${res.status}`);
      loadDetail(); onRefresh();
    } catch (err) { alert('Could not delete: ' + err.message); }
  };

  const st = STATUS[patient.status] || STATUS.ACTIVE;

  return (
    <div style={s.modalOverlay}>
      <div style={{ ...s.modalBox, maxWidth: '790px', maxHeight: '90vh' }}>
        <div style={{ ...s.modalHeader, background: '#336090' }}>
          <div>
            <div style={s.modalTitle}>{patient.patient_name}</div>
            <div style={s.modalSub}>
              {patient.payer} · {patient.loc} · Admitted {fmtDate(patient.admit_date)} · Day {patient.los}
              {patient.discharged && <span style={{ color: '#7dd3fc' }}> · DISCHARGED {fmtDate(patient.discharge_date)}</span>}
            </div>
          </div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 80px)' }}>

          {patient.why && (
            <div style={{ padding: '12px 24px', background: st.bg, color: st.text, fontSize: '13px', fontWeight: '600', borderBottom: `1px solid ${st.bar}33` }}>
              {st.label.toUpperCase()} — {patient.why}
              {patient.dollars_at_risk > 0 && ` · ${money(patient.dollars_at_risk)} at risk`}
            </div>
          )}

          <div style={s.detailSection}>
            <div style={s.detailGrid}>
              <div style={s.detailItem}><span style={s.detailLabel}>Assigned To</span><span style={s.detailValue}>{patient.assigned_to || '—'}</span></div>
              <div style={s.detailItem}><span style={s.detailLabel}>Day Rate (collected)</span><span style={s.detailValue}>{patient.day_rate ? money(patient.day_rate) + '/day' : '—'}</span></div>
              <div style={s.detailItem}><span style={s.detailLabel}>Member ID</span><span style={s.detailValue}>{patient.member_id || '—'}</span></div>
              <div style={s.detailItem}><span style={s.detailLabel}>Insurance Phone</span><span style={s.detailValue}>{patient.insurance_phone || '—'}</span></div>
              <div style={s.detailItem}><span style={s.detailLabel}>Dx Codes</span><span style={s.detailValue}>{patient.dx_codes || '—'}</span></div>
              <div style={s.detailItem}><span style={s.detailLabel}>Unauthorized Days <span onClick={() => setShowDayGrid(true)} title="Day-by-day coverage" style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 700 }}>[view days]</span></span>
                {showDayGrid && <DayGridModal patient={patient} token={token} onClose={() => setShowDayGrid(false)} />}
                <span style={{ ...s.detailValue, color: patient.unauthorized_days > 0 ? '#ef4444' : '#374151', fontWeight: '700' }}>
                  {patient.unauthorized_days || 0}{patient.denied_days > 0 && ` · ${patient.denied_days} denied`}
                  {(patient.unauth_dates && patient.unauth_dates.length) ? <span style={{ fontWeight: 400, fontSize: 12, color: '#991b1b' }}> — {(() => { const g = {}; patient.unauth_dates.forEach(d => { const m = new Date(d + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }); (g[m] = g[m] || []).push(String(+d.slice(8, 10))); }); return Object.entries(g).map(([m, ds]) => m + ' ' + ds.join(', ')).join(' · '); })()}</span> : null}
                </span></div>
              <div style={s.detailItem}><span style={s.detailLabel}>Internal Days (clinical)</span>
                <span style={{ ...s.detailValue, color: patient.internal_days > 0 ? '#6d28d9' : '#374151', fontWeight: patient.internal_days > 0 ? '700' : '500' }}>
                  {patient.internal_days || 0}
                </span></div>
              <div style={s.detailItem}><span style={s.detailLabel}>UR Due</span><span style={s.detailValue}>{fmtDate(patient.ur_due)}</span></div>
            </div>
          </div>

          <PrecertIcmBlock patient={patient} token={token} />

          <div style={s.detailSection}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={s.sectionTitle}>Authorization History</div>
              <button onClick={() => setShowAddAuth(true)} style={{ ...s.btnPrimary, padding: '6px 14px', fontSize: 12 }}>+ Add Auth</button>
            </div>
            {showAddAuth && (
              <AddAuthModal patient={patient} token={token}
                onClose={() => setShowAddAuth(false)}
                onSaved={() => { setShowAddAuth(false); loadDetail(); if (onRefresh) onRefresh(); }} />
            )}
            {loading ? <div style={s.loadingText}>Loading...</div> : auths.length === 0 ? (
              <div style={s.emptyText}>No authorizations on file</div>
            ) : (
              <table style={s.authTable}>
                <thead><tr>
                  {['LOC', 'Type', 'Dates', 'Req/Auth', 'Auth #', 'Status', ''].map((h, i) => <th key={i} style={s.authTh}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {auths.map(a => (
                    <tr key={a.id}>
                      {editAuth === a.id ? (
                        <td colSpan={7} style={{ padding: '12px' }}>
                          <div style={s.formGrid2}>
                            <div style={s.field}>
                              <label style={s.label}>Location / NPI</label>
                              <select value={editForm.facility_npi ? editForm.facility_npi + '|' + (editForm.facility_name || '') : ''}
                                onChange={e => {
                                  const [npi, ...rest] = e.target.value.split('|');
                                  setEditForm({ ...editForm, facility_npi: npi, facility_name: rest.join('|') });
                                }} style={s.input}>
                                <option value="">&mdash; select location &mdash;</option>
                                {editForm.facility_npi && !npiOptions.some(o => o.npi === editForm.facility_npi && o.location_name === editForm.facility_name) && (
                                  <option value={editForm.facility_npi + '|' + (editForm.facility_name || '')}>{editForm.facility_name} &mdash; {editForm.facility_npi} (current)</option>
                                )}
                                {npiOptions.map(o => (
                                  <option key={o.id} value={o.npi + '|' + o.location_name}>{o.location_name} &mdash; {o.npi}</option>
                                ))}
                              </select>
                            </div>
                            {[
                              { label: 'Level of Care', key: 'loc', type: 'select', options: LOC_OPTIONS },
                              { label: 'Status', key: 'auth_status', type: 'select', options: AUTH_STATUSES },
                              { label: 'Date From', key: 'date_from', type: 'date' },
                              { label: 'Date To', key: 'date_to', type: 'date' },
                              { label: 'Days Requested', key: 'days_requested', type: 'number' },
                              { label: 'Days Authorized', key: 'days_authorized', type: 'number' },
                              { label: 'Auth #', key: 'auth_number' },
                              { label: 'Denial Reason', key: 'denial_reason', type: 'select', options: DENIAL_REASONS },
                              { label: 'P2P Outcome', key: 'p2p_outcome' },
                              { label: 'Retro Outcome', key: 'retro_outcome', type: 'select', options: RETRO_OUTCOMES },
                              { label: 'CEO Decision', key: 'ceo_decision' },
                              { label: 'Notes', key: 'auth_notes' },
                            ].map(f => (
                              <div key={f.key} style={s.field}>
                                <label style={s.label}>{f.label}</label>
                                {f.type === 'select' ? (
                                  <select value={editForm[f.key]} onChange={e => setEditForm({ ...editForm, [f.key]: e.target.value })} style={s.input}>
                                    {f.options.map(o => <option key={o} value={o}>{o || 'Select...'}</option>)}
                                  </select>
                                ) : (
                                  <input type={f.type || 'text'} value={editForm[f.key]} onChange={e => setEditForm({ ...editForm, [f.key]: e.target.value })} style={s.input} />
                                )}
                              </div>
                            ))}
                            <div style={s.field}>
                              <label style={s.label}>Days of week (leave blank for every billable day)</label>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => { const sel = (editForm.auth_days_of_week || []).includes(i); return (
                                  <button key={d} type="button" onClick={() => setEditForm(f => { const cur = new Set(f.auth_days_of_week || []); if (cur.has(i)) cur.delete(i); else cur.add(i); return { ...f, auth_days_of_week: [...cur].sort() }; })}
                                    style={{ padding: '4px 9px', borderRadius: '6px', border: '1px solid #cbd5e1', background: sel ? '#336090' : '#fff', color: sel ? '#fff' : '#334155', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>{d}</button>); })}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                            <button onClick={() => saveEditAuth(a.id)} disabled={saving} style={s.saveBtn}>{saving ? 'Saving...' : 'Save'}</button>
                            <button onClick={() => setEditAuth(null)} style={s.cancelBtn}>Cancel</button>
                          </div>
                        </td>
                      ) : (
                        <>
                          <td style={s.authTd}><span style={{ ...s.locBadge, ...(LOC_COLORS[a.loc] || {}) }}>{a.loc}</span></td>
                          <td style={s.authTd}>
                            {a.auth_type === 'INTERNAL'
                              ? <span style={s.internalTag}>INTERNAL</span>
                              : <span style={{ fontSize: '11px', color: '#888' }}>payer</span>}
                          </td>
                          <td style={s.authTd}>{fmtDate(a.date_from)}<br /><span style={{ color: '#999', fontSize: '11px' }}>→ {fmtDate(a.date_to)}</span></td>
                          <td style={s.authTd}>
                            {a.days_requested || '—'} / {a.days_authorized || '—'}
                            {a.days_requested > a.days_authorized && (
                              <div style={{ color: '#991b1b', fontSize: '11px', fontWeight: '700' }}>
                                {a.days_requested - a.days_authorized} denied
                              </div>
                            )}
                          </td>
                          <td style={{ ...s.authTd, fontSize: '12px' }}>{a.auth_number || '—'}{a.facility_name && <div style={{ fontSize: '10px', color: '#888' }}>{a.facility_name}</div>}</td>
                          <td style={s.authTd}>
                            <span style={{
                              ...s.statusBadge,
                              background: a.auth_status === 'Approved' ? '#dcfce7' : a.auth_status === 'Denied' ? '#fef2f2' : '#f3f4f6',
                              color: a.auth_status === 'Approved' ? '#166534' : a.auth_status === 'Denied' ? '#991b1b' : '#374151',
                            }}>{a.auth_status}</span>
                            {a.retro_billed && <div style={{ fontSize: '10px', color: '#92400e', marginTop: '3px', fontWeight: '700' }}>RETRO: {a.retro_outcome || 'pending'}</div>}
                          </td>
                          <td style={s.authTd}><button onClick={() => startEditAuth(a)} style={s.editBtn}>Edit</button>{canDeleteAuth && <button onClick={() => deleteAuth(a)} style={{ ...s.editBtn, marginLeft: '4px', color: '#991b1b' }}>Delete</button>}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={s.detailSection}>
            <div style={s.sectionTitle}>Notes Log</div>
            {loading ? <div style={s.loadingText}>Loading...</div> : notes.length === 0 ? (
              <div style={s.emptyText}>No notes yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {notes.map(n => (
                  <div key={n.id} style={s.noteRow}>
                    {editNote === n.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <select value={noteForm.action_type}
                            onChange={e => setNoteForm({ ...noteForm, action_type: e.target.value })}
                            style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 12 }}>
                            {ACTION_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
                          </select>
                          <input value={noteForm.staff_initials} placeholder="Initials"
                            onChange={e => setNoteForm({ ...noteForm, staff_initials: e.target.value })}
                            style={{ width: 70, padding: '4px 6px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 12 }} />
                        </div>
                        <textarea value={noteForm.note_text} rows={3}
                          onChange={e => setNoteForm({ ...noteForm, note_text: e.target.value })}
                          style={{ padding: '6px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 12, fontFamily: 'inherit' }} />
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => saveEditNote(n.id)} disabled={saving}
                            style={{ padding: '4px 12px', borderRadius: 4, border: 'none', background: '#166534', color: '#fff', fontSize: 12, cursor: 'pointer' }}>
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button onClick={() => setEditNote(null)}
                            style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ ...s.noteMeta, alignItems: 'center' }}>
                          <span style={s.noteAction}>{n.action_type}</span>
                          <span style={s.noteStaff}>{n.staff_initials}</span>
                          <span style={s.noteDate}>{fmtDate(n.created_at)}</span>
                          <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                            <button onClick={() => startEditNote(n)} title="Edit"
                              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#475569', padding: 0 }}>Edit</button>
                            <button onClick={() => deleteNote(n)} title="Delete"
                              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#991b1b', padding: 0 }}>Delete</button>
                          </span>
                        </div>
                        <div style={s.noteText}>{n.note_text}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ASSIGN MODAL ───────────────────────────────────────────────
function AssignModal({ patient, token, onClose, onSaved }) {
  const [assignTo, setAssignTo] = useState(patient.assigned_to || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/ur/${patient.ur_patient_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ assigned_to: assignTo }),
      });
      onSaved();
      onClose();
    } catch (err) { alert('Failed to assign'); }
    setSaving(false);
  };

  return (
    <div style={s.modalOverlay}>
      <div style={{ ...s.modalBox, maxWidth: '360px' }}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>Assign UR</div>
          <div style={s.modalSub}>{patient.patient_name}</div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          <div style={s.field}>
            <label style={s.label}>Assign To</label>
            <input value={assignTo} onChange={e => setAssignTo(e.target.value)} placeholder="Staff name or initials" style={s.input} autoFocus />
          </div>
        </div>
        <div style={s.modalFooter}>
          <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={s.saveBtn}>{saving ? 'Saving...' : 'Assign'}</button>
        </div>
      </div>
    </div>
  );
}

// ── PATIENT CARD ───────────────────────────────────────────────
// Correct the discharge date/type on an already-discharged episode (audited old->new server-side)
function EditDcModal({ patient, token, onClose, onSaved }) {
  const [form, setForm] = useState({
    discharge_date: (patient.discharge_date || '').slice(0, 10),
    discharge_type: patient.discharge_type || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    if (!form.discharge_date || !form.discharge_type) { setErr('Discharge date and type are required.'); return; }
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API}/api/episodes/${patient.episode_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ discharge_date: form.discharge_date, discharge_type: form.discharge_type }),
      });
      let j = null;
      try { j = await r.json(); } catch (e) { j = null; }
      if (!r.ok || !j || j.ok !== true) { setErr((j && j.error) || ('Save failed (HTTP ' + r.status + ')')); setSaving(false); return; }
      onSaved(); onClose();
    } catch (e) { setErr('Connection error'); }
    setSaving(false);
  };
  return (
    <div style={s.modalOverlay}>
      <div style={{ ...s.modalBox, maxWidth: '400px' }}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>Edit Discharge</div>
          <div style={s.modalSub}>{patient.patient_name} · corrects the official discharge record (audited)</div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#5D6B7A', textTransform: 'uppercase', marginBottom: 4 }}>Discharge Date</label>
            <input type="date" value={form.discharge_date}
              onChange={e => setForm(f => ({ ...f, discharge_date: e.target.value }))}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #D8E3EE', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#5D6B7A', textTransform: 'uppercase', marginBottom: 4 }}>Discharge Type</label>
            <select value={form.discharge_type}
              onChange={e => setForm(f => ({ ...f, discharge_type: e.target.value }))}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #D8E3EE', fontSize: 13, width: '100%', boxSizing: 'border-box', background: '#fff' }}>
              <option value="">Select…</option>
              <option value="planned">Planned</option>
              <option value="unplanned">Unplanned</option>
            </select>
          </div>
          {err && <div style={{ color: '#B91C1C', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ background: 'none', border: '1px solid #D8E3EE', borderRadius: 7, padding: '8px 14px', fontSize: 12.5, cursor: 'pointer', color: '#5D6B7A' }}>Cancel</button>
            <button disabled={saving} onClick={save}
              style={{ background: '#0878E8', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save Correction'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// VOB window: the uploaded VOB document itself, embedded, with parsed fields
// on top when they exist. Opens from the card — staff never leave the board.
function VOBModal({ patient, token, onClose }) {
  const [vob, setVob] = useState('loading');
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/ur/${patient.ur_patient_id}/vob`, { headers: { Authorization: `Bearer ${token}` } });
        const j = await r.json();
        setVob(j.ok ? j : { has_vob: false });
      } catch (e) { setVob({ has_vob: false }); }
    })();
  }, [patient.ur_patient_id, token]);
  const vm = (n) => n == null ? null : '$' + Number(n).toLocaleString('en-US');
  const b = vob && vob.benefits;
  const fields = b ? [
    ['Payer', b.payer_name], ['Plan', b.plan_name],
    ['Member ID', b.member_id || b.policy_id], ['Group #', b.group_number],
    ['Deductible', b.deductible != null ? vm(b.deductible) + (b.deductible_met != null ? ' · met ' + vm(b.deductible_met) : '') : null],
    ['OOP Max', b.oop_max != null ? vm(b.oop_max) + (b.oop_met != null ? ' · met ' + vm(b.oop_met) : '') : null],
    ['Network', b.in_network == null ? null : (b.in_network ? 'In-network' : 'Out-of-network' + (b.oon_coverage_pct ? ' · ' + b.oon_coverage_pct + '%' : ''))],
    ['Auth Required', b.auth_required == null ? null : (b.auth_required ? 'Yes' : 'No')],
    ['RX BIN/PCN', [b.rx_bin, b.rx_pcn].filter(Boolean).join(' / ') || null],
  ].filter(([l, v]) => v) : [];
  // Portal to body: the card's hover transform hijacks position:fixed, so the
  // overlay must escape the card's stacking context entirely.
  return createPortal(
    <div style={s.modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...s.modalBox, maxWidth: '900px', maxHeight: '92vh' }}>
        <div style={{ ...s.modalHeader, background: '#336090' }}>
          <div>
            <div style={s.modalTitle}>VOB — {patient.patient_name}</div>
            <div style={s.modalSub}>{patient.payer}{b && b.verified_at ? ' · verified ' + fmtDate(b.verified_at) : ''}</div>
          </div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', maxHeight: 'calc(92vh - 80px)', padding: '16px 20px' }}>
          {vob === 'loading' ? <div style={{ color: '#5D6B7A', fontSize: '13px' }}>Loading VOB...</div>
          : !vob.has_vob ? (
            <div style={{ color: '#5D6B7A', fontSize: '13px', padding: '20px 0' }}>
              {vob.reason === 'no_lead' ? 'No VOB linked — this episode has no lead attached.' : 'No VOB on file for this patient.'}
            </div>
          ) : (
            <>
              {fields.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px 18px', marginBottom: '14px', background: '#F4F8FB', border: '1px solid #D8E3EE', borderRadius: '10px', padding: '12px 14px' }}>
                  {fields.map(([l, v]) => (
                    <div key={l}>
                      <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#5D6B7A' }}>{l}</div>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: '#22303E' }}>{v}</div>
                    </div>
                  ))}
                  {b && b.secondary_payer_name && (
                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #D8E3EE', paddingTop: '8px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#E99A18' }}>Secondary Payer</div>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>{b.secondary_payer_name}{b.secondary_member_id ? ' · ' + b.secondary_member_id : ''}{b.secondary_plan_name ? ' · ' + b.secondary_plan_name : ''}</div>
                    </div>
                  )}
                </div>
              )}
              {vob.pdf_url ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#5D6B7A' }}>
                      VOB Document{vob.pdf_kind === 'prelim' ? ' (prelim)' : ''}
                    </span>
                    <a href={vob.pdf_url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', color: '#0878E8', fontWeight: 700, fontSize: '12.5px', textDecoration: 'none' }}>Open in new tab →</a>
                  </div>
                  <iframe title="VOB document" src={vob.pdf_url}
                    style={{ width: '100%', height: '62vh', border: '1px solid #D8E3EE', borderRadius: '10px', background: '#fff' }} />
                </>
              ) : (
                <div style={{ color: '#5D6B7A', fontSize: '13px' }}>Benefit fields on file — no VOB document uploaded.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function PatientCard({ p, onAddAuth, onAddNote, onDetail, onAssign, onCloseUR, onReopenUR, onMarkGap, onEditInfo, onRetro, onDischarge, onEditDc }) {
  const _canDischarge = (() => { try { const _p = JSON.parse(localStorage.getItem('tcio_emr_perms') || '{}'); return !!(_p.discharge || _p.superuser); } catch (e) { return false; } })();
  const st = STATUS[p.status] || STATUS.ACTIVE;
  const [hover, setHover] = useState(false);
  const [showVob, setShowVob] = useState(false);

  // Clicking anywhere on the card opens the full record.
  // Buttons stopPropagation so they still do their own thing.
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  return (
    <div
      onClick={() => onDetail(p)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="Click to open the full record"
      style={{
        ...s.card,
        borderLeftColor: st.bar,
        background: st.bg,
        cursor: 'pointer',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? '0 4px 14px rgba(0,0,0,0.10)' : 'none',
        transition: 'transform 0.12s, box-shadow 0.12s',
      }}
    >
      {p.ur_closed ? (
        <div style={{ ...s.dcBanner, background: '#E8EEF4', color: '#5D6B7A', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span>CLOSED — discharged {fmtDate(p.discharge_date)}. Reopen to send or correct anything on this stay.</span>
          <button onClick={(e) => { e.stopPropagation(); onReopenUR && onReopenUR(p); }} style={{ border: '1px solid #D8E3EE', background: '#fff', color: '#0878E8', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Reopen</button>
        </div>
      ) : p.discharged && (
        <div style={s.dcBanner}>
          DISCHARGED {fmtDate(p.discharge_date)} — stays on the UR board until closed. Auths still land after a patient leaves.
        </div>
      )}
      {p.why && (
        <div style={{ ...s.whyBar, background: 'rgba(0,0,0,0.03)', color: st.text }}>
          {p.why}
        </div>
      )}
      <div style={s.cardTop}>
        <div style={s.cardLeft}>
          <div style={s.cardName}>{p.patient_name}</div>
          <div style={s.cardMeta}>
            <strong>{p.payer}</strong> · {p.assigned_to || 'unassigned'} · day {p.los}
            {!p.discharged && ` · bed ${p.bed_id || '—'}`}
          </div>
          <div style={s.cardMeta}>
            {p.loc && <span style={{ ...s.locBadge, ...(LOC_COLORS[p.loc] || {}) }}>{p.loc}</span>}
            {p.auth_number
              ? <span style={s.authInfo}>Auth# {p.auth_number} · thru {fmtDate(p.auth_thru)}</span>
              : p.auth_count > 0
                ? <span style={s.authInfo}>thru {fmtDate(p.auth_thru)}</span>
                : <span style={s.noAuth}>No auth on file</span>}
            {p.internal_days > 0 && <span style={s.internalTag}>{p.internal_days}d INTERNAL</span>}
            {p.exception_days > 0 && <span style={{ ...s.internalTag, background: '#dcfce7', color: '#166534' }}>{p.exception_days}d {(p.exception_types || []).map(t => EXCEPTION_LABEL[t] || t).join('/')}</span>}
            {p.day_rate > 0 && <span style={{ fontSize: '12px', color: '#888' }}>{money(p.day_rate)}/day</span>}
          </div>
          {p.ur_due && !p.discharged && <div style={s.urDue}>UR due: <strong>{fmtDate(p.ur_due)}</strong></div>}
        </div>
        <div style={s.cardRight}>
          <div style={{ ...s.statusTag, background: st.tagBg, color: st.tag, marginBottom: '8px' }}>{st.label.toUpperCase()}</div>
          {p.dollars_at_risk > 0 && (
            <>
              <div style={s.moneyBig}>{money(p.dollars_at_risk)}</div>
              <div style={s.moneySub}>{p.unauthorized_days} unauth days</div>
            </>
          )}
        </div>
      </div>
      <div style={s.cardActions}>
        <button onClick={stop(() => onAddAuth(p))} style={s.btnPrimary}>Add Auth</button>
        <button onClick={stop(() => onAddNote(p, 'Submitted Clinical'))} style={s.btnGreen}>Log Submission</button>
        <button onClick={stop(() => onAddNote(p, 'Auth Received'))} style={s.btnGreen}>Auth Received</button>
        <button onClick={stop(() => onAddNote(p, 'Auth Denied'))} style={s.btnRed}>Log Denial</button>
        <button onClick={stop(() => setShowVob(true))} style={{ ...s.btnOutline, color: '#336090', borderColor: '#B9CFE4', fontWeight: 700 }}>VOB</button>
        {showVob && <VOBModal patient={p} token={localStorage.getItem('tcio_token')} onClose={() => setShowVob(false)} />}
        <button onClick={stop(() => onAddNote(p, 'Note'))} style={s.btnOutline}>+ Note</button>
        <button onClick={stop(() => onAssign(p))} style={s.btnOutline}>Assign</button>
        <button onClick={stop(() => onMarkGap(p))} style={s.btnOutline}>Mark Gap</button>
        <button onClick={stop(() => onEditInfo(p))} style={s.btnOutline}>Edit Info</button>
        {_canDischarge && onDischarge && <button onClick={stop(() => onDischarge(p))} style={{ ...s.btnOutline, color: '#E5484D', borderColor: '#FBD9DC' }}>Discharge</button>}
        <button onClick={stop(() => onRetro(p))} style={{ ...s.btnOutline, color: '#A66B0B', borderColor: '#FCEBC9' }}>Confirm Retro</button>
        {p.discharged && _canDischarge && onEditDc && (
          <button onClick={stop(() => onEditDc(p))} style={{ ...s.btnOutline, color: '#0878E8', borderColor: '#BFDBFE' }}>Edit DC</button>
        )}
        {p.discharged && (
          <button onClick={stop(() => onCloseUR(p))} style={s.btnClose}>✓ Close UR</button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#9ca3af', alignSelf: 'center', fontWeight: 600 }}>
          {p.auth_count > 0 ? `${p.auth_count} auth${p.auth_count === 1 ? '' : 's'} on file · ` : ''}click for full record →
        </span>
      </div>
    </div>
  );
}

// ── MAIN ───────────────────────────────────────────────────────
export default function URModule({ user }) {
  const token = user?.token || localStorage.getItem('tcio_token');

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [windows, setWindows] = useState({});
  const [loading, setLoading] = useState(true);
  const [lane, setLane] = useState('ACTION');
  const [showClosed, setShowClosed] = useState(false);   // closed UR records of the last 90 days, on request (Tina 11/1)
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);

  const [facilities, setFacilities] = useState([]);
  // No hardcoded facility: default to the first facility in the user's scope.
  const [newAdmitIds, setNewAdmitIds] = useState(new Set());
  const [facilityId, setFacilityId] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/facilities`, { headers: { Authorization: `Bearer ${token}` } });
        const j = await r.json();
        if (j.ok) {
          setFacilities(j.data);
          // Shared facility selection with EMR (Tina two-tabs fix): honor last pick anywhere.
          let shared = null;
          try { shared = JSON.parse(localStorage.getItem('tcio_facility') || 'null'); } catch (e) {}
          const sharedOk = shared && shared.id && j.data.some(f => f.id === shared.id);
          if (j.data.length) setFacilityId(prev => prev || (sharedOk ? shared.id : j.data[0].id));
        }
      } catch (e) {}
    })();
  }, []);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ur/risk?facilityId=${facilityId}${showClosed ? '&include_closed=1' : ''}`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.ok) { setData(j.data); setSummary(j.summary); setWindows(j.payer_windows || {}); }
      else console.error('UR risk error:', j.error);
    } catch (err) { console.error('UR load error:', err); }
    setLoading(false);
  }, [token, facilityId, showClosed]);

  useEffect(() => { load(); }, [load]);

  const closeUR = async (p) => {
    const warn = p.unauthorized_days > 0
      ? `WARNING: ${p.unauthorized_days} days are still unauthorized (${money(p.dollars_at_risk)}). Closing means giving up on those auths.`
      : 'Fully authorized. Safe to close.';
    if (!window.confirm(`Close UR for ${p.patient_name}?\n\n${warn}`)) return;
    try {
      await fetch(`${API}/api/ur/${p.ur_patient_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'Closed' }),
      });
      load();
    } catch (err) { alert('Failed to close UR'); }
  };
  const reopenUR = async (p) => {
    if (!window.confirm(`Reopen UR for ${p.patient_name}? They go back on the board as Active.`)) return;
    try {
      await fetch(`${API}/api/ur/${p.ur_patient_id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ status: 'Active' }) });
      load();
    } catch (err) { alert('Failed to reopen UR'); }
  };

  const closeModal = () => setModal(null);
  const refreshAndClose = () => { load(); setModal(null); };

  const ACTION_STATES = ['MISSED', 'EXPIRED', 'NO_DATES', 'PAYER_STALL', 'POST_DC'];
  const inLane = (p) =>
    lane === 'ALL' ? true
      : lane === 'NEW_ADMIT' ? newAdmitIds.has(p.ur_patient_id)
      : lane === 'ACTION' ? ACTION_STATES.includes(p.status)
        : lane === 'POST_DC' ? (p.status === 'POST_DC' || p.status === 'POST_DC_CLEAR')
          : p.status === lane;

  const filtered = data.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q
      || p.patient_name?.toLowerCase().includes(q)
      || p.payer?.toLowerCase().includes(q)
      || p.assigned_to?.toLowerCase().includes(q);
    // When searching, ignore the lane filter — find the patient wherever they are.
    // A name search that only looks inside the selected lane hides patients who exist.
    return matchSearch && (q ? true : (lane === 'LOCATION' ? Boolean(p.location_check) : lane === 'CHASE' ? ['MISSED', 'EXPIRED', 'NO_DATES'].includes(p.status) : inLane(p)));
  });

  const sumFor = (states) => data
    .filter(p => states.includes(p.status))
    .reduce((n, p) => n + (p.dollars_at_risk || 0), 0);

  const stallPayers = Object.entries(windows).filter(([, w]) => w.stall).map(([k]) => k);
  const S = summary || {};
  const postDcTotal = (S.post_dc || 0) + (S.post_dc_clear || 0);

  const LANES = [
    { key: 'ACTION',      label: 'Needs Action',     n: (S.missed || 0) + (S.expired || 0) + (S.no_dates || 0) + (S.payer_stall || 0) + (S.post_dc || 0), color: '#991b1b', money: sumFor(ACTION_STATES) },
    { key: 'LOCATION',    label: '📍 Check Location', n: S.location_check || 0, color: '#b45309', money: 0 },
    { key: 'NEW_ADMIT',   label: '🆕 New Admit',  n: newAdmitIds.size, color: '#2563eb', money: 0 },
    { key: 'MISSED',      label: '🔴 Missed',        n: S.missed || 0,      color: '#991b1b', money: sumFor(['MISSED']) },
    { key: 'EXPIRED',     label: '🔴 Expired',       n: S.expired || 0,     color: '#dc2626', money: sumFor(['EXPIRED']) },
    { key: 'NO_DATES',    label: '🟠 No Dates',      n: S.no_dates || 0,    color: '#b45309', money: sumFor(['NO_DATES']) },
    { key: 'PAYER_STALL', label: '🟠 Payer Stall',   n: S.payer_stall || 0, color: '#c2410c', money: sumFor(['PAYER_STALL']) },
    { key: 'PENDING_SLOW', label: '🟠 Pending — Slow', n: S.pending_slow || 0, color: '#ea580c', money: sumFor(['PENDING_SLOW']) },
    { key: 'POST_DC',     label: '🔵 Post-Discharge', n: postDcTotal,       color: '#0891b2', money: sumFor(['POST_DC']) },
    { key: 'EXPIRING',    label: '🟡 Expiring (3 days)',      n: S.expiring || 0,    color: '#f59e0b', money: data.filter(p => p.status === 'EXPIRING').reduce((n, p) => n + (p.week_exposure || 0), 0), moneyNote: 'next 7 days' },
    { key: 'PENDING',     label: '🟡 Pending',       n: S.pending || 0,     color: '#eab308', money: sumFor(['PENDING']) },
    { key: 'ACTIVE',      label: '🟢 Covered',       n: S.active || 0,      color: '#22c55e', money: 0 },
  ];

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Utilization Review</h2>
          <p style={s.subtitle}>
            {summary
              ? `${S.total_patients} on the board · ${S.active} covered · ${S.unauthorized_days} unauthorized days${postDcTotal ? ` · ${postDcTotal} post-discharge` : ''}`
              : 'Loading...'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select value={facilityId} onChange={e => { const v = e.target.value; setFacilityId(v); try { const f = facilities.find(x => x.id === v); localStorage.setItem('tcio_facility', JSON.stringify({ id: v, name: f ? (f.org_name || f.name) : '' })); } catch (err) {} }}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 600, background: '#fff', cursor: 'pointer' }}>
            {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
          </select>
          <button onClick={load} style={s.refreshBtn}>↻ Refresh</button>
        </div>
      </div>

      <DueTodayPanel token={token} facilityId={facilityId}
        onFeed={f => setNewAdmitIds(new Set(((f && f.new_admits) || []).map(x => x.ur_patient_id)))}
        onOpenPatient={r => { const p = data.find(x => x.ur_patient_id === r.ur_patient_id); if (p) setModal({ type: 'detail', patient: p }); }} />

      {summary && (
        <div style={s.riskBanner}>
          <div>
            <div style={s.riskBig}>{money(S.dollars_at_risk)}</div>
            <div style={s.riskLabel}>At Risk — Care Delivered, Not Authorized</div>
          </div>
          <div style={s.riskDiv} />
          <div onClick={() => setLane('CHASE')} title="Show these patients" style={{ cursor: 'pointer' }}>
            <div style={s.riskBig}>{(S.missed || 0) + (S.expired || 0) + (S.no_dates || 0)}</div>
            <div style={s.riskLabel}>Patients Someone Must Chase</div>
          </div>
          <div style={s.riskDiv} />
          <div>
            <div style={s.riskBig}>{S.denied_days || 0}</div>
            <div style={s.riskLabel}>Denied Days</div>
          </div>
          <div style={s.riskNote}>
            {S.pending || 0} pending auths ({money(S.dollars_pending)}) are in flight and normal — excluded from risk.
            {stallPayers.length > 0 && <><br />Payer stall: <strong>{stallPayers.join(', ')}</strong></>}
          </div>
        </div>
      )}

      <div style={s.laneSummary}>
        {LANES.map(L => (
          <div key={L.key} onClick={() => setLane(L.key)} style={{
            ...s.laneCard,
            borderColor: lane === L.key ? L.color : '#e5e7eb',
            background: lane === L.key ? '#fafafa' : 'white',
          }}>
            <div style={{ ...s.laneCount, color: L.color }}>{L.n}</div>
            <div style={s.laneLabel}>{L.label}</div>
            {L.money > 0 && <div style={{ ...s.laneMoney, color: L.color }}>{money(L.money)}{L.moneyNote ? ' ' + L.moneyNote : ''}</div>}
          </div>
        ))}
      </div>

      <div style={s.searchBar}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by patient, payer, or assigned staff..." style={s.searchInput} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#5D6B7A', marginLeft: 10, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          <input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} /> Show closed (90 days)
        </label>
        {search && <button onClick={() => setSearch('')} style={s.clearBtn}>✕</button>}
      </div>

      {loading ? (
        <div style={s.empty}>Loading UR patients...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>
          {lane === 'ACTION' ? 'Nothing needs action. Every patient is authorized.' : 'No patients in this lane.'}
        </div>
      ) : (
        <div style={s.cardList}>
          {filtered.map(p => (
            <PatientCard
              key={p.ur_patient_id}
              p={p}
              onAddAuth={x => setModal({ type: 'auth', patient: x })}
              onAddNote={(x, action) => setModal({ type: 'note', patient: x, defaultAction: action })}
              onDetail={x => setModal({ type: 'detail', patient: x })}
              onAssign={x => setModal({ type: 'assign', patient: x })}
              onCloseUR={closeUR}
              onReopenUR={reopenUR}
              onMarkGap={x => setModal({ type: 'gap', patient: x })}
              onEditInfo={x => setModal({ type: 'editinfo', patient: x })}
              onDischarge={x => setModal({ type: 'discharge', patient: x })}
              onRetro={x => setModal({ type: 'retro', patient: x })}
              onEditDc={x => setModal({ type: 'editdc', patient: x })}
            />
          ))}
        </div>
      )}

      {modal?.type === 'auth' && <AddAuthModal patient={modal.patient} token={token} onClose={closeModal} onSaved={refreshAndClose} />}
      {modal?.type === 'note' && <AddNoteModal patient={modal.patient} token={token} defaultAction={modal.defaultAction} onClose={closeModal} onSaved={load} />}
      {modal?.type === 'detail' && <PatientDetailModal patient={modal.patient} token={token} onClose={closeModal} onRefresh={load} />}
      {modal?.type === 'assign' && <AssignModal patient={modal.patient} token={token} onClose={closeModal} onSaved={refreshAndClose} />}
      {modal?.type === 'gap' && <MarkGapModal patient={modal.patient} token={token} onClose={closeModal} onSaved={load} />}
      {modal?.type === 'editinfo' && <EditEpisodeModal patient={modal.patient} token={token} onClose={closeModal} onSaved={refreshAndClose} />}
      {modal?.type === 'discharge' && <DischargeModal patient={modal.patient} token={token} onClose={closeModal} onSaved={refreshAndClose} />}
      {modal?.type === 'retro' && <RetroBillModal patient={modal.patient} token={token} onClose={closeModal} onSaved={load} />}
      {modal?.type === 'editdc' && <EditDcModal patient={modal.patient} token={token} onClose={closeModal} onSaved={refreshAndClose} />}
    </div>
  );
}
